import React from 'react';
import InfoPopover from './InfoPopover';
import {
  knowledgeBase,
  KnowledgeBaseKey,
} from '../../../data/admin-knowledge-base';

export interface SectionHeadProps {
  /** Optional micro-cap context label rendered above the title (e.g. "Activity"). */
  eyebrow?: string;
  /** The section heading. */
  title: string;
  /** Supporting copy beneath the title — caps at 64 ch in the layout. */
  caption?: React.ReactNode;
  /**
   * KnowledgeBase key — when set, renders an InfoPopover next to the title
   * with operations-team explanations sourced from
   * `src/data/admin-knowledge-base.ts`.
   */
  infoKey?: KnowledgeBaseKey;
  /** Right-aligned actions (Export CSV button, AddButton, etc.). */
  actions?: React.ReactNode;
  /** Heading level. Defaults to h2. Use h3 for deeper sub-sections. */
  as?: 'h2' | 'h3';
}

/**
 * Editorial section heading inside the admin canvas. The eyebrow → title →
 * caption flows as a left-aligned column block; an optional info icon anchors
 * immediately right of the title; right-side actions sit far right.
 *
 * Single source of truth for in-canvas section heads — replaces the bespoke
 * `.admin-dashboard-orders__head` pattern that was duplicated per page.
 */
export default function SectionHead({
  eyebrow,
  title,
  caption,
  infoKey,
  actions,
  as: Heading = 'h2',
}: SectionHeadProps) {
  const entry = infoKey ? knowledgeBase[infoKey] : null;
  return (
    <header className="admin-section-head">
      <div className="admin-section-head__lede">
        {eyebrow && (
          <span className="admin-section-head__eyebrow">{eyebrow}</span>
        )}
        <div className="admin-section-head__title-row">
          <Heading className="admin-section-head__title">{title}</Heading>
          {entry && <InfoPopover entry={entry} />}
        </div>
        {caption && (
          <p className="admin-section-head__caption">{caption}</p>
        )}
      </div>
      {actions && (
        <div className="admin-section-head__actions">{actions}</div>
      )}
    </header>
  );
}
